import React from 'react';
import { Link } from 'react-router-dom';
import extract1 from '../extract-1.png';
import extract2 from '../Extract-2.png';
import extract3 from '../extract-3.png';
import extract4 from '../extract-4.png';
import extract5 from '../extract-5.png';
import extract6 from '../extract-6.png';
import octagonAlert from '../octagon-alert.svg';
import monitorX from '../monitor-x.svg';
import fileStack from '../file-stack.svg';
import openai from '../openai.png';
import docai from '../docai.png';
import { useScrollToTop } from '../hooks/useScrollToTop';
import './CaseStudy.css';

function CaseStudy1() {
  useScrollToTop();
  return (
    <div className="case-study-page">
      <div className="case-study-container">
        <Link to="/" className="back-button">
          <i className="fas fa-arrow-left"></i>
          <span>Back to all Projects</span>
        </Link>
        
        <div className="case-study-headline">
          <h1>Using DocAI to eliminate manual data entry and improve efficiency for Loan Administrators processing draws.</h1>
          <p className="internal-case-study-body">The current commercial construction draw submission process is manual, fragmented, and error-prone. On average, subcontractors on a construction project can expect to wait anywhere from 30 to 90 days to get paid, after submitting their invoice.</p>
        </div>
        
        <div className="case-study-section">
          <div className="case-study-left-column">
            <div className="info-pair">
              <span className="info-label">Timeline</span>
              <span className="info-text">2025</span>
            </div>
            <div className="info-pair">
              <span className="info-label">Tech Stack</span>
              <span className="info-text">DocAI</span>
            </div>
            <div className="info-pair">
              <span className="info-label">ICP Targeted</span>
              <span className="info-text">CRE Loan Administrators</span>
            </div>
            <div className="info-pair">
              <span className="info-label">My Role</span>
              <span className="info-text">Product Designer</span>
            </div>
          </div>
          
          <div className="case-study-right-column">
            <div className="section-content">
              <span className="info-label">Background</span>
              <h2>The Problem</h2>
              <p className="internal-case-study-body">Construction loan administrators spend countless hours manually reviewing and processing draw requests, leading to delays in payments and increased risk of errors. The current process involves multiple stakeholders, complex documentation, and manual data entry that creates bottlenecks in the construction financing workflow.</p>
            </div>
          </div>
        </div>

        <div className="case-study-section" style={{ marginTop: '4.5rem'}}>
            <div className="image-container" style={{ width: '100%'}}>
            <img src={extract5} alt="Case Study 1" style={{ width: '100%', height: 'auto' }} />
            </div>
        </div>

            <div className="margin-top-72 section-content">
              <span className="info-label">Problem to Solve</span>
              <h2>The hidden costs of manual review processes</h2>
            </div>

        
        <div className="case-study-section-slim margin-top-48">
          <div className="two-column-layout">
            <div className="column">
              <img src={octagonAlert} alt="Alert Icon" className="content-icon" />
              <h3 className="content-heading">Risk of human error</h3>
              <p className="internal-case-study-body">Misclassified or missed documents can lead to incorrect disbursements or compliance violations.</p>
            </div>
            <div className="column">
              <img src={monitorX} alt="Monitor Icon" className="content-icon" />
              <h3 className="content-heading">Operational inefficiencies</h3>
              <p className="internal-case-study-body">Manual sorting, data entry, and validation significantly slow down turn times.</p>
            </div>
          </div>
        </div>

        <div className="case-study-section-slim 32px-margin-top" style={{ marginTop: '32px' }}>
          <div className="two-column-layout">
            <div className="column">
              <img src={fileStack} alt="File Stack Icon" className="content-icon" />
              <h3 className="content-heading">Compliance Overhead</h3>
              <p className="internal-case-study-body">Verifying the presence and accuracy of required documentation is difficult to scale (IE is this a Lien Waiver or just a screenshot of a random document)</p>
            </div>
            <div className="column">
              {/* Right column intentionally left blank */}
            </div>
          </div>
        </div>
        
        <div className="margin-top-72 section-content">
              <span className="info-label">Technical Approach</span>
              <h2>Training models to understand construction draws</h2>
              <p className="internal-case-study-body">The model to support this intelligent draw builder was trained using both DocumentAI and OpenAI by parsing hundreds of draw packet examples for the model to be able to understand what these documentation examples looked like. </p>
              <p className="internal-case-study-body">The team's evaluation method for OpenAI was capped around 0.66 while DocumentAI was able to reach 0.951.</p>
        </div>
        
        <div className="case-study-section-slim" style={{ marginTop: '32px' }}>
          <div className="two-column-layout">
            <div className="column">
              <img src={openai} alt="OpenAI" style={{ width: '100%', height: 'auto' }} />
            </div>
            <div className="column">
              <img src={docai} alt="Document AI" style={{ width: '100%', height: 'auto' }} />
            </div>
          </div>
        </div>
        
        <div className="margin-top-72 section-content">
              <span className="info-label">Solution</span>
              <h2>Split and Classify</h2>
              <p className="internal-case-study-body">When a draw package is uploaded, the system splits the PDF into recognizable documents, classifies each by type, generates a draft draw with relevant data extracted.</p>
              <p className="internal-case-study-body">Provides side-by-side UI for reviewing document splits, correcting errors, and reclassifying documents.</p>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={extract1} alt="Case Study 1" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={extract2} alt="Case Study 1" style={{ width: '100%', height: 'auto' }} />
        </div>        
        
        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={extract3} alt="Case Study 1" style={{ width: '100%', height: 'auto' }} />
        </div>
        
        <div className="margin-top-72 section-content">
              <span className="info-label">Solution</span>
              <h2>Async Process</h2>
              <p className="internal-case-study-body">To reduce time on task further, we generated an 'inbox' where Borrowers could email their draw package to rather than the Lender being a middle man bottleneck. </p>
              <p className="internal-case-study-body">The system provides real-time status updates for the various stages of the intelligent draw builder process so the user has full visibility into what's happening and the length of waiting time. </p>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={extract4} alt="Case Study 1" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 section-content">
              <span className="info-label">Solution</span>
              <h2>Extract Data</h2>
              <p className="internal-case-study-body">Automatically compiles a draft draw based on documentation and populates draw budget and invoice amounts from extracted data. </p>
              <p className="internal-case-study-body">UI indicates what data has been extracted so the user can easily parse what was system generated versus human input / override. </p>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={extract5} alt="Case Study 1" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={extract6} alt="Case Study 1" style={{ width: '100%', height: 'auto' }} />
        </div>

      </div>
    </div>
  );
}

export default CaseStudy1;
